import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BadgeCheck, Download, Loader2, Share2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { mediaUrl } from "@/lib/upload";
import { Logo } from "@/components/fsl/Logo";
import { ErrorState, LoadingState } from "@/components/fsl/States";

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;
const mb = (b) => (b ? `${(b / 1048576).toFixed(1).replace(".", ",")} MB` : "");

function ShareButton({ d }) {
  const [busy, setBusy] = useState(false);
  if (typeof navigator === "undefined" || !navigator.share) return null;
  const share = async () => {
    setBusy(true);
    try {
      const blob = await (await fetch(mediaUrl(d.download_url))).blob();
      const file = new File([blob], d.filename, { type: d.content_type || blob.type });
      if (navigator.canShare && !navigator.canShare({ files: [file] })) throw new Error("no-files");
      await navigator.share({ files: [file], title: d.title });
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Condivisione non disponibile su questo dispositivo: usa «Salva sul telefono»");
    } finally { setBusy(false); }
  };
  return <button type="button" className="btn-ghost h-12 w-full text-base" onClick={share} disabled={busy} data-testid="delivery-share"><span className="inline-flex items-center gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} {busy ? "Preparo il file…" : d.kind === "video" ? "Salva nei Video / Condividi" : "Salva in Foto / Condividi"}</span></button>;
}

export default function Delivery() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/payments/delivery/${token}`).then((r) => { if (r.data.redirect) navigate(r.data.redirect, { replace: true }); else setD(r.data); }).catch(setErr); }, [token, navigate]);
  if (err) return <ErrorState message={apiError(err)} />;
  if (!d) return <LoadingState full />;
  const src = mediaUrl(d.stream_url);
  return (
    <div className="min-h-screen bg-ink-950 text-fsl-white">
      <div className="mx-auto max-w-[720px] px-5 py-8 sm:py-12 space-y-6" data-testid="delivery-page">
        <div className="flex justify-center"><Logo /></div>
        <div className="fsl-card-gold p-5 sm:p-8 relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
          <div className="relative space-y-5">
            <div className="fsl-kicker flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-fsl-success" /> Acquisto confermato · {d.kind === "video" ? "Video della gara" : "Foto della gara"}</div>
            <div>
              <h1 className="font-display font-extrabold uppercase text-3xl sm:text-4xl leading-[0.95]" data-testid="delivery-title">{d.title}</h1>
              <div className="text-sm text-fsl-slate mt-1">{[d.tournament?.name, d.match, d.kickoff_at ? fmtDate(d.kickoff_at) : null].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="rounded-2xl overflow-hidden bg-black border border-white/10" data-testid="delivery-preview">
              {d.kind === "video" ? <video key={src} src={src} controls playsInline preload="metadata" className="w-full max-h-[70vh]" data-testid="delivery-video" /> : <img src={src} alt={d.title} className="w-full max-h-[70vh] object-contain" data-testid="delivery-image" />}
            </div>
            <div className="grid gap-3">
              <a className="btn-gold h-12 w-full text-base" href={mediaUrl(d.download_url)} download={d.filename} data-testid="delivery-download"><Download className="h-4 w-4" /> Salva sul telefono {d.size ? <span className="num text-xs opacity-80">· {mb(d.size)}</span> : null}</a>
              <ShareButton d={d} />
            </div>
            <p className="text-xs text-fsl-slate">Su iPhone: tieni premuto il video o la foto e scegli «Salva», oppure usa il pulsante Condividi per salvarlo nel rullino. Questo link è riservato al tuo acquisto ({eur(d.amount)}): conservalo, resta sempre valido.</p>
            <Link to="/account" className="text-xs text-fsl-gold hover:underline inline-flex items-center gap-1" data-testid="delivery-account"><UserRound className="h-3.5 w-3.5" /> Tutti i miei acquisti in Area genitori</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
